/* =============================================================================
   dashUtils.js (TB-073)
   Shared helpers for the post-login dashboards: appointment date formatting
   (wall-clock time, see below), a small session cache, session reading and
   the service base URLs. No raw colours here.
   ========================================================================== */
import moment from 'moment'
import { getEstProviderView } from '@/utils/estDoctorSelection'
import { formatWall, formatInstant, viewToday } from '@/utils/locale'

export const TX = process.env.VUE_APP_TRANSACTIONS_SERVICE_API
export const PROVIDERS = process.env.VUE_APP_PROVIDERS_SERVICE_API
export const AUTH_PROVIDER = process.env.VUE_APP_API_BASEURL_PROVIDER
export const AUTH_ESTABLISHMENT = process.env.VUE_APP_API_BASEURL_ESTABLISHMENT

// ---------------------------------------------------------------- dates
// Appointment times (startTime / endTime) hold the clinic wall-clock time and
// come back as "<wall time>Z". TB-123: wall values, "now" and display all go
// through utils/locale (clinic clock from the clinicTimeZone setting; shown in
// clinic or the viewer's zone per the display mode).
export function fmtDate (value) {
  return value ? formatWall(value, 'date') : ''
}

export function fmtTime (value) {
  return value ? formatWall(value, 'time') : ''
}

export function fmtDateTime (value) {
  const d = fmtDate(value)
  const t = fmtTime(value)
  return d && t ? `${d} · ${t}` : d
}

// "Today", "Tomorrow" or the date; used as a short chip on rows.
export function fmtRelativeDay (value) {
  if (!value) return ''
  const day = formatWall(value, 'iso')
  if (!day) return ''
  const diff = moment.utc(day).diff(moment.utc(viewToday()), 'days')
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Tomorrow'
  if (diff === -1) return 'Yesterday'
  return formatWall(value, 'dayMonth')
}

// Notification createdAt is a real instant: shown relative, then as a date
// in the same format as the rows.
export function fmtSince (value) {
  if (!value) return ''
  const m = moment(value)
  if (!m.isValid()) return ''
  const mins = moment().diff(m, 'minutes')
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} h ago`
  return formatInstant(value, 'date')
}

export function greeting (now = moment()) {
  const h = now.hours()
  if (h < 12) return 'Good morning'
  if (h < 18) return 'Good afternoon'
  return 'Good evening'
}

export function todayLong () {
  return formatInstant(Date.now(), 'long')
}

// Month keys (YYYY-MM) that the given window touches.
export function monthsBetween (start, end) {
  const out = []
  const m = moment(start).startOf('month')
  const last = moment(end).startOf('month')
  while (m.isSameOrBefore(last)) {
    out.push(m.format('YYYY-MM'))
    m.add(1, 'month')
  }
  return out
}

// ---------------------------------------------------------------- counts
// TB-092: the patient "Completed" count. The Appointments page lists, under
// Completed, the API's completed bucket plus visits whose status is still
// over-due or awaiting-invoice (the visit happened, the invoice is not
// closed). The dashboard tile must count the same set, or it shows 7 while
// the tab shows 9.
export const COMPLETED_EXTRA_STATUSES = ['over-due', 'awaiting-invoice']

// Sum of meta.total over the completed response and the extra-status
// responses. Extra statuses are best effort: a missing or failed one counts 0.
export function completedTotal (completed, extras = []) {
  const total = (d) => {
    if (!d) return 0
    const t = d.meta && d.meta.total
    if (typeof t === 'number' && Number.isFinite(t)) return t
    return Array.isArray(d.appointments) ? d.appointments.length : 0
  }
  return extras.reduce((sum, d) => sum + total(d), total(completed))
}

// ---------------------------------------------------------------- session
export function readUser () {
  try {
    return JSON.parse(localStorage.getItem('user')) || JSON.parse(localStorage.getItem('userInfo')) || {}
  } catch (e) {
    return {}
  }
}

// TB-085 (DEF-229): per-tab selection, not the shared localStorage key
export function readEstProvider () {
  return getEstProviderView()
}

export function isSignedIn () {
  try {
    return !!localStorage.getItem('accessToken')
  } catch (e) {
    return false
  }
}

export function fullName (p) {
  if (!p) return ''
  return p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ') || p.name || ''
}

export function initials (name) {
  return (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('')
}

export function specialities (detail) {
  const s = detail && detail.specialities
  if (!s) return ''
  if (Array.isArray(s)) return s.map((x) => (x && (x.title || x.name)) || x).filter((x) => typeof x === 'string').join(', ')
  return typeof s === 'string' ? s.split(',').map((x) => x.trim()).filter(Boolean).join(', ') : ''
}

// ---------------------------------------------------------------- cache
// Per-session cache keyed by user id + request key. Values live for
// TTL_MS; a focus refresh within that window reuses them.
const TTL_MS = 60 * 1000
const store = new Map()

function sessionKey () {
  const u = readUser()
  let tok = ''
  try { tok = (localStorage.getItem('accessToken') || '').slice(-12) } catch (e) {}
  return `${u.uid || u.userId || ''}:${tok}`
}

let lastSession = null

// Concurrent callers share one in-flight request; a different session (other
// user or new token) drops everything cached for the previous one.
export function cached (key, loader, { force = false } = {}) {
  const sk = sessionKey()
  if (lastSession !== null && lastSession !== sk) store.clear()
  lastSession = sk
  const k = `${sk}|${key}`
  const hit = store.get(k)
  if (hit && hit.promise) return hit.promise
  if (!force && hit && Date.now() - hit.at < TTL_MS) return Promise.resolve(hit.value)
  const promise = Promise.resolve()
    .then(loader)
    .then((value) => {
      store.set(k, { at: Date.now(), value })
      return value
    })
    .catch((e) => {
      store.delete(k)
      throw e
    })
  store.set(k, { at: 0, promise })
  return promise
}

export function clearDashCache () {
  store.clear()
}

// Settle a list of promises into [{ok, value|error}] so one failing card
// never blanks the whole page.
export async function settle (promises) {
  const res = await Promise.allSettled(promises)
  return res.map((r) => (r.status === 'fulfilled' ? { ok: true, value: r.value } : { ok: false, error: r.reason }))
}
